/**
 * Demo store — in-memory, scope-of-session.
 *
 * Mantenemos el estado en un objeto del módulo. Mientras la pestaña esté
 * abierta y el bundle JS vivo, las mutaciones persisten — el visitante
 * puede crear / editar / borrar y se ve reflejado en cualquier vista.
 *
 * Al recargar la página el módulo se re-importa y el seed inicial vuelve
 * a correr → cada visita arranca limpia (es lo deseado para un live demo
 * en la landing — sin contaminación entre visitantes).
 *
 * No usamos localStorage ni sessionStorage adrede.
 */

const memory: Record<string, unknown> = {}

export function readCollection<T>(name: string): T[] {
  const cur = memory[name]
  return Array.isArray(cur) ? (cur as T[]) : []
}

export function writeCollection<T>(name: string, items: T[]): void {
  memory[name] = items
}

export function readSingleton<T>(name: string): T | null {
  const cur = memory[name]
  return (cur ?? null) as T | null
}

export function writeSingleton<T>(name: string, value: T): void {
  memory[name] = value
}

/** Siembra solo si no había nada antes. Idempotente dentro del lifecycle del módulo. */
export function ensureSeeded<T>(name: string, seed: T[] | T, kind: 'collection' | 'singleton' = 'collection'): void {
  if (memory[name] !== undefined) return
  if (kind === 'collection') {
    memory[name] = seed
  } else {
    memory[name] = seed
  }
}

// ─── CRUD helpers tipados para colecciones con campo `id`. ────────────

interface HasId {
  id: string
}

export function findById<T extends HasId>(name: string, id: string): T | null {
  return readCollection<T>(name).find((row) => row.id === id) ?? null
}

export function insertOne<T extends HasId>(name: string, row: T): T {
  const items = readCollection<T>(name)
  writeCollection(name, [row, ...items])
  return row
}

export function updateOne<T extends HasId>(name: string, id: string, patch: Partial<T>): T | null {
  const items = readCollection<T>(name)
  let updated: T | null = null
  const next = items.map((row) => {
    if (row.id !== id) return row
    updated = { ...row, ...patch, id }
    return updated
  })
  if (!updated) return null
  writeCollection(name, next)
  return updated
}

export function removeOne<T extends HasId>(name: string, id: string): boolean {
  const items = readCollection<T>(name)
  const next = items.filter((row) => row.id !== id)
  if (next.length === items.length) return false
  writeCollection(name, next)
  return true
}

// ─── Generador de IDs corto + decente. ─────────────────────────────────

export function genId(prefix: string): string {
  const time = Date.now().toString(36)
  const rand = Math.random().toString(36).slice(2, 6)
  return `${prefix}_${time}${rand}`
}

// ─── Reset (botón "Reiniciar demo"). Vuelve a sembrar todo. ───────────

export function clearAll(): void {
  for (const key of Object.keys(memory)) delete memory[key]
}
